import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2,
  }).format(Number(amount || 0));
}

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    fullName: '',
    shortName: '',
    duration: '',
    registrationFee: '',
    universityPayment: '',
    coursePayment: '',
    scheduleType: 'weekday',
  });

  const accessHeaderValue = useMemo(
    () => (isSuperAdmin ? 'all' : accessibleCourseIds.join(',')),
    [accessibleCourseIds, isSuperAdmin]
  );

  const accessHeaders = useMemo(() => (
    accessHeaderValue ? { 'x-accessible-courses': accessHeaderValue } : {}
  ), [accessHeaderValue]);

  const hasFullAccess = isSuperAdmin;

  const editableCourseIds = useMemo(() => {
    if (hasFullAccess) {
      return courses.map((c) => c.courseId);
    }
    return Object.entries(permissions)
      .filter(([, value]) => value?.isUpdate)
      .map(([key]) => Number(key));
  }, [courses, hasFullAccess, permissions]);

  const deletableCourseIds = useMemo(() => {
    if (hasFullAccess) {
      return courses.map((c) => c.courseId);
    }
    return Object.entries(permissions)
      .filter(([, value]) => value?.isDelete)
      .map(([key]) => Number(key));
  }, [courses, hasFullAccess, permissions]);

  const visibleCourses = useMemo(() => {
    if (hasFullAccess) {
      return courses;
    }
    return courses.filter((course) => accessibleCourseIds.includes(course.courseId));
  }, [courses, accessibleCourseIds, hasFullAccess]);

  const filteredCourses = useMemo(() => visibleCourses.filter((course) => (
    course.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.shortName.toLowerCase().includes(searchTerm.toLowerCase())
  )), [visibleCourses, searchTerm]);

  const resetForm = () => {
    setFormData({
      fullName: '',
      shortName: '',
      duration: '',
      registrationFee: '',
      universityPayment: '',
      coursePayment: '',
      scheduleType: 'weekday',
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.length > 500) {
      newErrors.fullName = 'Full name must not exceed 500 characters';
    }

    if (!formData.shortName.trim()) {
      newErrors.shortName = 'Short name is required';
    } else if (formData.shortName.length > 255) {
      newErrors.shortName = 'Short name must not exceed 255 characters';
    }

    if (!formData.duration) {
      newErrors.duration = 'Duration is required';
    } else if (Number(formData.duration) < 1 || Number(formData.duration) > 120) {
      newErrors.duration = 'Duration must be between 1 and 120 months';
    }

    if (!formData.registrationFee) {
      newErrors.registrationFee = 'Registration fee is required';
    } else if (Number(formData.registrationFee) < 0) {
      newErrors.registrationFee = 'Registration fee must be positive';
    }

    if (!formData.universityPayment) {
      newErrors.universityPayment = 'University payment is required';
    } else if (Number(formData.universityPayment) < 0) {
      newErrors.universityPayment = 'University payment must be positive';
    }

    if (!formData.coursePayment) {
      newErrors.coursePayment = 'Course payment is required';
    } else if (Number(formData.coursePayment) < 0) {
      newErrors.coursePayment = 'Course payment must be positive';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateFullName = async (fullName, scheduleType, excludeId) => {
    if (!fullName.trim()) {
      return true;
    }

    try {
      const resp = await axiosInstance.get('/admin/courses/check-full-name', {
        params: { fullName, scheduleType, courseId: excludeId },
        headers: accessHeaders,
      });
      if (resp.data?.exists) {
        setErrors((prev) => ({ ...prev, fullName: 'A course with this name and schedule type already exists' }));
        return false;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Full name check failed', err);
    }
    return true;
  };

  const validateShortName = async (shortName, excludeId) => {
    if (!shortName.trim()) {
      return true;
    }

    try {
      const resp = await axiosInstance.get('/admin/courses/check-short-name', {
        params: { shortName, courseId: excludeId },
        headers: accessHeaders,
      });
      if (resp.data?.exists) {
        setErrors((prev) => ({ ...prev, shortName: 'This short name already exists' }));
        return false;
      }
    } catch (err) {
      console.error('Short name check failed', err);
    }
    return true;
  };

  const loadAccessAndCourses = async () => {
    setLoading(true);
    setError('');
    try {
      const accessResp = await axiosInstance.get('/admin/me/coordinate-courses');
      const accessData = accessResp.data || {};
      const ids = Array.isArray(accessData.accessibleCourseIds) ? accessData.accessibleCourseIds : [];
      const isSa = Boolean(accessData.isSuperAdmin);

      setAccessibleCourseIds(ids);
      setPermissions(accessData.coordinateCourses || {});
      setIsSuperAdmin(isSa);

      const headerValue = isSa ? 'all' : ids.join(',');
      const courseResp = await axiosInstance.get('/admin/courses', {
        headers: headerValue ? { 'x-accessible-courses': headerValue } : {},
      });
      if (courseResp.data?.success) {
        setCourses(courseResp.data.courses || []);
      } else {
        setCourses([]);
        setError(courseResp.data?.message || 'Unable to load courses');
      }
    } catch (err) {
      console.error('Failed to load courses', err);
      setError(err.response?.data?.message || 'Failed to load courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccessAndCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = () => {
    if (!hasFullAccess) {
      Swal.fire('Permission denied', 'Only admins can create courses.', 'error');
      return;
    }
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (course) => {
    if (!hasFullAccess && !editableCourseIds.includes(course.courseId)) {
      Swal.fire('Permission denied', 'You can only edit courses you coordinate.', 'error');
      return;
    }
    setSelectedCourse(course);
    setFormData({
      fullName: course.fullName,
      shortName: course.shortName,
      duration: course.duration,
      registrationFee: course.registrationFee,
      universityPayment: course.universityPayment,
      coursePayment: course.coursePayment,
      scheduleType: course.scheduleType,
    });
    setErrors({});
    setShowEditModal(true);
  };

  const handleViewDetails = (course) => {
    setSelectedCourse(course);
    setShowDetailsModal(true);
  };

  const handleDelete = (course) => {
    if (!hasFullAccess && !deletableCourseIds.includes(course.courseId)) {
      Swal.fire('Permission denied', 'You can only delete courses you coordinate.', 'error');
      return;
    }
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${course.shortName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        axiosInstance.delete(`/admin/courses/${course.courseId}`, { headers: accessHeaders })
          .then(() => {
            Swal.fire('Deleted!', 'Course has been deleted.', 'success');
            loadAccessAndCourses();
          })
          .catch((err) => {
            const msg = err.response?.data?.message || 'Failed to delete course';
            Swal.fire('Error', msg, 'error');
          });
      }
    });
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const fullNameOk = await validateFullName(formData.fullName, formData.scheduleType);
    if (!fullNameOk) return;
    const shortNameOk = await validateShortName(formData.shortName);
    if (!shortNameOk) return;

    setIsSubmitting(true);
    axiosInstance.post('/admin/courses', formData, { headers: accessHeaders })
      .then((resp) => {
        if (resp.data?.success) {
          Swal.fire('Success', 'Course added successfully', 'success');
          setShowAddModal(false);
          resetForm();
          loadAccessAndCourses();
        } else {
          Swal.fire('Error', resp.data?.message || 'Failed to add course', 'error');
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'Failed to add course';
        Swal.fire('Error', msg, 'error');
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!selectedCourse) return;
    if (!validateForm()) return;
    if (!hasFullAccess && !editableCourseIds.includes(selectedCourse.courseId)) {
      Swal.fire('Permission denied', 'You can only edit courses you coordinate.', 'error');
      return;
    }

    setIsSubmitting(true);
    axiosInstance.put(`/admin/courses/${selectedCourse.courseId}`, formData, { headers: accessHeaders })
      .then((resp) => {
        if (resp.data?.success) {
          Swal.fire('Success', 'Course updated successfully', 'success');
          setShowEditModal(false);
          resetForm();
          setSelectedCourse(null);
          loadAccessAndCourses();
        } else {
          Swal.fire('Error', resp.data?.message || 'Failed to update course', 'error');
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'Failed to update course';
        Swal.fire('Error', msg, 'error');
      })
      .finally(() => setIsSubmitting(false));
  };

  const totalCourses = visibleCourses.length;

  return (
    <DashboardLayout title="Courses">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Coordinator Courses</h1>
            <p className="mt-1 text-sm text-gray-600">Courses you coordinate; admins see all.</p>
          </div>
          {hasFullAccess && (
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Course
            </button>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-indigo-100 p-3">
              <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Courses</p>
              <p className="text-2xl font-bold text-gray-900">{loading ? '...' : totalCourses}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-4 shadow-sm border border-gray-200">
          <div className="relative">
            <input
              type="text"
              placeholder="Search courses by name or short code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <svg
              className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                  {hasFullAccess && (
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Course ID</th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Course Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Short Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Schedule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Course Fee</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={hasFullAccess ? 8 : 7} className="px-6 py-6 text-center text-gray-500">Loading courses...</td>
                  </tr>
                ) : filteredCourses.length > 0 ? (
                  filteredCourses.map((course, index) => (
                    <tr key={course.courseId} className="hover:bg-gray-50 transition-colors">
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{index + 1}</td>
                      {hasFullAccess && (
                        <td className="whitespace-nowrap px-6 py-4 text-xs text-gray-500">{course.courseId}</td>
                      )}
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-md">{course.fullName}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">{course.shortName}</span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{course.duration} months</td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          course.scheduleType === 'weekday'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {course.scheduleType === 'weekday' ? 'Weekday' : 'Weekend'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 font-medium">{formatCurrency(course.coursePayment)}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewDetails(course)}
                            className="rounded-lg bg-gray-100 px-3 py-1.5 text-gray-700 hover:bg-gray-200 transition-colors"
                            title="View Details"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {(hasFullAccess || editableCourseIds.includes(course.courseId)) && (
                            <button
                              onClick={() => handleEdit(course)}
                              className="rounded-lg bg-blue-100 px-3 py-1.5 text-blue-700 hover:bg-blue-200 transition-colors"
                              title="Edit"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {(hasFullAccess || deletableCourseIds.includes(course.courseId)) && (
                            <button
                              onClick={() => handleDelete(course)}
                              className="rounded-lg bg-red-100 px-3 py-1.5 text-red-700 hover:bg-red-200 transition-colors"
                              title="Delete"
                            >
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1 1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={hasFullAccess ? 8 : 7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <p className="mt-2 text-sm">No courses found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Course</h2>
            <form onSubmit={handleSubmitAdd} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Course Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    onBlur={() => validateFullName(formData.fullName, formData.scheduleType)}
                    className={`w-full rounded-lg border ${errors.fullName ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="e.g., BEng (Hons) in Software Engineering"
                    autoFocus
                  />
                  {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Short Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.shortName}
                    onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                    onBlur={() => validateShortName(formData.shortName)}
                    className={`w-full rounded-lg border ${errors.shortName ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="e.g., LMU-T-BENG-SE"
                  />
                  {errors.shortName && <p className="mt-1 text-sm text-red-500">{errors.shortName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (months) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className={`w-full rounded-lg border ${errors.duration ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="12"
                    min="1"
                    max="120"
                  />
                  {errors.duration && <p className="mt-1 text-sm text-red-500">{errors.duration}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Fee (LKR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.registrationFee}
                    onChange={(e) => setFormData({ ...formData, registrationFee: e.target.value })}
                    className={`w-full rounded-lg border ${errors.registrationFee ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="20000.00"
                    min="0"
                  />
                  {errors.registrationFee && <p className="mt-1 text-sm text-red-500">{errors.registrationFee}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    University Payment (LKR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.universityPayment}
                    onChange={(e) => setFormData({ ...formData, universityPayment: e.target.value })}
                    className={`w-full rounded-lg border ${errors.universityPayment ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="900.00"
                    min="0"
                  />
                  {errors.universityPayment && <p className="mt-1 text-sm text-red-500">{errors.universityPayment}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Payment (LKR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.coursePayment}
                    onChange={(e) => setFormData({ ...formData, coursePayment: e.target.value })}
                    className={`w-full rounded-lg border ${errors.coursePayment ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="330000.00"
                    min="0"
                  />
                  {errors.coursePayment && <p className="mt-1 text-sm text-red-500">{errors.coursePayment}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.scheduleType}
                    onChange={(e) => {
                      const scheduleType = e.target.value;
                      setFormData({ ...formData, scheduleType });
                      if (formData.fullName.trim()) validateFullName(formData.fullName, scheduleType);
                    }}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="weekday">Weekday</option>
                    <option value="weekend">Weekend</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Adding...' : 'Add Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedCourse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Course</h2>
            <form onSubmit={handleSubmitEdit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Course Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    disabled={!hasFullAccess}
                    title={!hasFullAccess ? 'Only Admin can change Full Course Name' : undefined}
                    className={`w-full rounded-lg border ${errors.fullName ? 'border-red-500' : 'border-gray-300'} px-4 py-2 ${!hasFullAccess ? 'bg-gray-100 cursor-not-allowed' : 'focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500'}`}
                    autoFocus
                  />
                  {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
                  {!hasFullAccess && (
                    <p className="mt-1 text-xs text-gray-500">Only Admin can change the Full Course Name.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Short Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.shortName}
                    onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                    className={`w-full rounded-lg border ${errors.shortName ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  />
                  {errors.shortName && <p className="mt-1 text-sm text-red-500">{errors.shortName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (months) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className={`w-full rounded-lg border ${errors.duration ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    min="1"
                    max="120"
                  />
                  {errors.duration && <p className="mt-1 text-sm text-red-500">{errors.duration}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Fee (LKR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.registrationFee}
                    onChange={(e) => setFormData({ ...formData, registrationFee: e.target.value })}
                    className={`w-full rounded-lg border ${errors.registrationFee ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    min="0"
                  />
                  {errors.registrationFee && <p className="mt-1 text-sm text-red-500">{errors.registrationFee}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    University Payment (LKR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.universityPayment}
                    onChange={(e) => setFormData({ ...formData, universityPayment: e.target.value })}
                    className={`w-full rounded-lg border ${errors.universityPayment ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    min="0"
                  />
                  {errors.universityPayment && <p className="mt-1 text-sm text-red-500">{errors.universityPayment}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Payment (LKR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.coursePayment}
                    onChange={(e) => setFormData({ ...formData, coursePayment: e.target.value })}
                    className={`w-full rounded-lg border ${errors.coursePayment ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    min="0"
                  />
                  {errors.coursePayment && <p className="mt-1 text-sm text-red-500">{errors.coursePayment}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.scheduleType}
                    onChange={(e) => setFormData({ ...formData, scheduleType: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="weekday">Weekday</option>
                    <option value="weekend">Weekend</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); resetForm(); setSelectedCourse(null); }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailsModal && selectedCourse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Course Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Full Course Name</h3>
                <p className="text-gray-900">{selectedCourse.fullName}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Short Name</h3>
                  <p className="text-gray-900 font-medium">{selectedCourse.shortName}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Duration</h3>
                  <p className="text-gray-900">{selectedCourse.duration} months</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Schedule Type</h3>
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                    selectedCourse.scheduleType === 'weekday'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {selectedCourse.scheduleType === 'weekday' ? 'Weekday' : 'Weekend'}
                  </span>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Registration Fee</h3>
                  <p className="text-gray-900 font-medium">{formatCurrency(selectedCourse.registrationFee)}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">University Payment</h3>
                  <p className="text-gray-900 font-medium">{formatCurrency(selectedCourse.universityPayment)}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Course Payment</h3>
                  <p className="text-gray-900 font-medium">{formatCurrency(selectedCourse.coursePayment)}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Created At</h3>
                  <p className="text-gray-900 text-sm">
                    {new Date(selectedCourse.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Last Updated</h3>
                  <p className="text-gray-900 text-sm">
                    {new Date(selectedCourse.updatedAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>

              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h3 className="text-sm font-medium text-indigo-800 mb-2">Total Course Cost</h3>
                <p className="text-2xl font-bold text-indigo-900">
                  {formatCurrency(
                    Number(selectedCourse.registrationFee) +
                    Number(selectedCourse.universityPayment) +
                    Number(selectedCourse.coursePayment)
                  )}
                </p>
                <p className="text-xs text-indigo-700 mt-1">Registration + University + Course Fees</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              {(hasFullAccess || editableCourseIds.includes(selectedCourse.courseId)) && (
                <button
                  onClick={() => { setShowDetailsModal(false); handleEdit(selectedCourse); }}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                >
                  Edit Course
                </button>
              )}
              <button
                onClick={() => setShowDetailsModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
